import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { sendMessage } from '../../store/chatBotSlice';

interface ChatInputProps {
  sessionId: string | null;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  sessionId,
  disabled = false,
  placeholder = "Type your message...",
  onFocus,
  onBlur
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, isConnected } = useSelector((state: RootState) => state.chatBot);
  
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  // Focus textarea when component mounts
  useEffect(() => {
    if (textareaRef.current && !disabled && isConnected) {
      textareaRef.current.focus();
    }
  }, [disabled, isConnected]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessageHandler();
  };

  const sendMessageHandler = () => {
    if (!message.trim() || !sessionId || isLoading || !isConnected) {
      return;
    }

    const messageToSend = message.trim();
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    dispatch(sendMessage({ content: messageToSend, sessionId }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Send message on Enter (but not Shift+Enter)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageHandler();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) onBlur();
  };

  const isInputDisabled = disabled || !sessionId || !isConnected;
  const canSend = message.trim().length > 0 && !isLoading && !isInputDisabled;

  return (
    <div
      className="chat-input-container"
      style={{
        backgroundColor: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '8px 16px'
      }}
    >
      <form 
        onSubmit={handleSubmit} 
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '12px'
        }}
      >
        
        {/* Message Input */}
        <div style={{
          flex: 1,
          position: 'relative'
        }}>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={
              !isConnected
                ? "Connecting..."
                : !sessionId
                  ? "Starting session..."
                  : placeholder
            }
            disabled={isInputDisabled}
            rows={1}
            style={{
              width: '100%',
              resize: 'none',
              borderRadius: '6px',
              border: `2px solid ${isFocused ? '#99ebff' : '#D9F0FF'}`,
              padding: '10px 14px',
              paddingRight: '48px',
              outline: 'none',
              backgroundColor: !isConnected ? '#f9fafb' : '#FAFCFD',
              color: isInputDisabled ? '#6b7280' : '#2C3E50',
              cursor: isInputDisabled ? 'not-allowed' : 'text',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              maxHeight: '128px',
              overflowY: 'auto',
              minHeight: '40px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '1.5',
              boxShadow: isFocused
                ? '0 0 0 4px rgba(217, 240, 255, 0.4), 0 4px 12px rgba(33, 155, 206, 0.15)'
                : '0 2px 6px rgba(217, 240, 255, 0.3)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#CBD5E0 transparent'
            }}
            onMouseEnter={(e) => {
              if (!isFocused && !isInputDisabled) {
                e.currentTarget.style.borderColor = '#b9eefa';
              }
            }}
            onMouseLeave={(e) => {
              if (!isFocused) {
                e.currentTarget.style.borderColor = '#D9F0FF';
              }
            }}
          />
          
          {/* Character Counter (when approaching limit) */}
          {message.length > 800 && (
            <div style={{
              position: 'absolute',
              bottom: '8px',
              right: '48px',
              fontSize: '12px',
              color: message.length > 1000 ? '#ef4444' : '#f97316'
            }}>
              {message.length}/1000
            </div>
          )}
          
          {/* Character counter or status - removed duplicate loader */}
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!canSend}
          style={{
            width: '42px',
            height: '42px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: canSend ? '2px solid #b9eefa' : '2px solid #E5E7EB',
            cursor: canSend ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            outline: 'none',
            background: canSend
              ? 'linear-gradient(135deg, #EDF8FB 0%, #D9EEF7 100%)'
              : 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
            padding: 0,
            transform: 'scale(1)',
            boxShadow: canSend
              ? '0 4px 12px rgba(193, 228, 251, 0.3), 0 2px 4px rgba(0, 0, 0, 0.06)'
              : '0 2px 4px rgba(0, 0, 0, 0.06)'
          }}
          onMouseEnter={(e) => {
            if (canSend) {
              e.currentTarget.style.transform = 'scale(1.08) translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 0 0 4px rgba(193, 228, 251, 0.25), 0 8px 20px rgba(193, 228, 251, 0.5), 0 4px 8px rgba(0, 0, 0, 0.12)';
            }
          }}
          onMouseLeave={(e) => {
            if (canSend) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(193, 228, 251, 0.4), 0 2px 4px rgba(0, 0, 0, 0.08)';
            }
          }}
          title={
            !isConnected
              ? "Disconnected"
              : !sessionId
                ? "Starting session..."
                : !message.trim()
                  ? "Enter a message"
                  : "Send message (Enter)"
          }
        >
          <svg
            style={{
              width: '20px',
              height: '20px',
              transform: 'rotate(45deg)',
              color: canSend ? '#0f3340' : '#9ca3af',
              transition: 'all 0.2s'
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </form>

      {/* Input Help Text */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '8px',
        padding: '0 4px'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#6b7280'
        }}>
          {!isConnected 
            ? "Waiting for connection..." 
            : "Press Enter to send, Shift+Enter for new line"
          }
        </div>
        
        {/* Connection Status */}
        {!isConnected && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            color: '#f97316'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              backgroundColor: '#fb923c',
              borderRadius: '50%',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}></div>
            <span>Reconnecting...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInput;